/**
 * Student Requests Component
 * Handles the checklist for students and approval list for warehouse
 */

const StudentRequests = {
    render(selectedProjectId = null) {
        if (AuthManager.isStudent() && !AuthManager.canManageWarehouse()) {
            return this.renderStudentView(selectedProjectId);
        } else {
            return this.renderWarehouseView();
        }
    },

    // ===================================
    // Student View (Request Form)
    // ===================================
    renderStudentView(selectedProjectId = null) {
        const categories = InventoryService.categoriesInUse();
        const user = AuthManager.getCurrentUser();
        const studentId = AuthManager.getStudentId();
        const student = StudentService.getStudent(studentId);
        const formation = student?.formations?.length ? FormationService.getFormation(student.formations[0]) : null;
        const sections = categories.map(category => this.renderCategorySection(category, student)).join('');
        const escape = value => Utils.sanitizeHTML(String(value ?? ''));

        return `
            <div class="student-request-container dnr-proposal sr-view">
                <header class="sr-header">
                    <h2>Pedir material</h2>
                    <span class="sr-formation">${escape(formation?.name || 'Habilitado')}</span>
                </header>

                <form id="student-request-form" class="sr-form" ${UIEvents.attr('submit', function(event){event.preventDefault();StudentRequests.submitRequest();})}>
                    <div class="sr-main">
                        <section class="sr-card sr-project" aria-labelledby="sr-project-title">
                            <h3 id="sr-project-title" class="sr-step"><b>1</b> Proyecto y fechas</h3>
                            <div class="sr-project-row">
                                <div class="sr-field">
                                    <label class="sr-label" for="project-selector">Proyecto</label>
                                    <select class="form-select" id="project-selector" name="projectId"
                                        ${UIEvents.attr('change', function(){StudentRequests.handleProjectChange(this.value);})}>
                                        <option value="">Elige un proyecto</option>
                                        ${ProjectService.getProjectsForStudent(studentId, user.username).map(p => `
                                            <option value="${escape(p.id)}" ${selectedProjectId === p.id ? 'selected' : ''}>${escape(p.name)}</option>
                                        `).join('')}
                                        <option value="new">+ Crear nuevo proyecto…</option>
                                    </select>
                                </div>
                                <div id="date-selection-block" class="sr-dates">
                                    <div class="sr-field">
                                        <label class="sr-label" for="sr-from-date">Recoges</label>
                                        <input id="sr-from-date" type="datetime-local" class="form-control" name="fromDate" required ${UIEvents.attr('change', function(){StudentRequests.renderOrder();})}>
                                    </div>
                                    <div class="sr-field">
                                        <label class="sr-label" for="sr-to-date">Devuelves</label>
                                        <input id="sr-to-date" type="datetime-local" class="form-control" name="toDate" required ${UIEvents.attr('change', function(){StudentRequests.renderOrder();})}>
                                    </div>
                                </div>
                            </div>
                            <div id="new-project-container" class="sr-new-project" style="display:none">
                                <label class="sr-label" for="new-project-name">Nombre del proyecto</label>
                                <div class="sr-new-project-row">
                                    <input type="text" id="new-project-name" class="form-control" placeholder="Ej.: Corto «Marea»">
                                    <button type="button" class="btn btn-secondary" ${UIEvents.attr('click', function(){StudentRequests.openParticipantsModal();})}>
                                        <i class="fas fa-users" aria-hidden="true"></i> Elegir compañeros y crear
                                    </button>
                                </div>
                                <p id="project-help-text" class="sr-help">Pon el nombre y las fechas; después eliges a tus compañeros de rodaje.</p>
                            </div>
                            <p id="sr-project-dates" class="sr-project-dates" hidden></p>
                            <div id="sr-project-team" class="sr-project-team" hidden></div>
                            <div id="sr-team-panel" class="sr-team-panel" hidden></div>
                        </section>

                        <section class="sr-card sr-material" aria-labelledby="sr-material-title">
                            <div class="sr-material-head">
                                <h3 id="sr-material-title" class="sr-step"><b>2</b> Material</h3>
                                <input type="search" id="sr-material-search" class="form-control sr-material-search" placeholder="Buscar material" aria-label="Buscar material"
                                    ${UIEvents.attr('input', function(){StudentRequests.filterMaterial(this.value);})}>
                            </div>
                            <div class="inventory-checklist">
                                ${sections || '<div class="sr-empty"><i class="fas fa-box-open" aria-hidden="true"></i><p>No hay material disponible para su formación.</p></div>'}
                            </div>
                        </section>
                    </div>

                    <aside class="sr-side">
                        <section class="sr-card sr-order" aria-labelledby="sr-order-title">
                            <h3 id="sr-order-title" class="sr-step"><b>3</b> Tu pedido</h3>
                            <p id="sr-selection-summary" class="sr-summary" role="status" aria-live="polite">Ningún artículo seleccionado</p>
                            <ul id="sr-order-list" class="sr-order-list"></ul>
                            <p id="sr-order-dates" class="sr-order-dates"></p>
                            <label class="sr-label" for="sr-comments">Para qué lo necesitas</label>
                            <textarea id="sr-comments" class="form-control" name="comments" rows="3"
                                placeholder="Ej.: rodaje del corto en exterior, práctica de iluminación en plató…"></textarea>
                            <button type="submit" class="btn btn-primary sr-send" ${sections ? '' : 'disabled'}>
                                <i class="fas fa-paper-plane" aria-hidden="true"></i> Enviar solicitud de material
                            </button>
                        </section>
                        <section class="sr-card sr-history" aria-labelledby="sr-history-title">
                            <h3 id="sr-history-title" class="sr-side-title">Mis solicitudes</h3>
                            <div id="my-requests-list">${this.renderMyRequestsList()}</div>
                        </section>
                    </aside>
                </form>
            </div>
        `;
    },
    renderCategorySection(category, student) {
        // Find student allowed keys (formation IDs + academic years)
        const sFormations = student ? (student.formations || []) : [];
        const studentAllowedKeys = [...sFormations];
        sFormations.forEach(fId => {
            const formation = FormationService.getFormation(fId);
            if (formation && formation.academicYear) {
                studentAllowedKeys.push(formation.academicYear.toString());
            }
        });

        // Filter items by category AND if student has access
        const items = InventoryService.getItemsByCategory(category.id).filter(item => {
            if (item.status !== 'available') return false;
            const itemAllowed = item.allowedCourses || [];
            return itemAllowed.some(key => studentAllowedKeys.includes(key));
        });

        if (items.length === 0) return '';

        return `
            <div class="sr-category">
                <h4><i class="fas ${Utils.sanitizeHTML(category.icon)}" aria-hidden="true"></i> ${Utils.sanitizeHTML(category.label)}</h4>
                <div class="sr-items">
                    ${items.map(item => `
                        <div class="checklist-item sr-item">
                            <label class="sr-item-choice">
                                <input type="checkbox" name="items" value="${Utils.sanitizeHTML(item.id)}">
                                <span class="sr-item-photo">
                                    ${item.photo ? `<img src="${Utils.sanitizeHTML(item.photo)}" alt="" width="56" height="56" loading="lazy">` : '<i class="fas fa-tools" aria-hidden="true"></i>'}
                                </span>
                                <span class="sr-item-info">
                                    <strong>${Utils.sanitizeHTML(item.name)}</strong>
                                    <span class="sr-stock"><i class="fas fa-check-circle" aria-hidden="true"></i> Disponible: ${Number(item.quantity) || 0}</span>
                                </span>
                            </label>
                            <div class="qty-control" style="display:none">
                                <label for="qty-${Utils.sanitizeHTML(item.id)}">Cantidad</label>
                                <div class="sr-stepper">
                                    <button type="button" aria-label="Disminuir cantidad" ${UIEvents.attr('click', function(event,captured){StudentRequests.changeQty(this,-1,captured[0]);}, [item.quantity])}><i class="fas fa-minus" aria-hidden="true"></i></button>
                                    <input type="number" id="qty-${Utils.sanitizeHTML(item.id)}" value="1" min="1" max="${Number(item.quantity) || 0}"
                                        ${UIEvents.attr('change', function(event,captured){StudentRequests.validateQty(this,captured[0]);}, [item.quantity])}>
                                    <button type="button" aria-label="Aumentar cantidad" ${UIEvents.attr('click', function(event,captured){StudentRequests.changeQty(this,1,captured[0]);}, [item.quantity])}><i class="fas fa-plus" aria-hidden="true"></i></button>
                                </div>
                            </div>
                        </div>
                    `).join('')}
                </div>
            </div>
        `;
    },

    renderMyRequestsList() {
        const requests = WarehouseService.getStudentRequests(AuthManager.getStudentId())
            .slice().sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
        if (requests.length === 0) return '<div class="sr-empty"><i class="fas fa-clipboard-list" aria-hidden="true"></i><p>Aún no has pedido material.</p></div>';
        const labels = {pending:'Pendiente', approved:'Aprobada · por recoger', picked_up:'Fuera', returned:'Devuelta', rejected:'Rechazada', cancelled:'Cancelada'};
        const day = v => { const d = new Date(v); return isNaN(d) ? '' : d.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' }); };
        return `<ul class="sr-requests">${requests.map(r => {
            const project = ProjectService.getProjectById(r.projectId);
            const items = (r.items || []).map(i => {
                const item = typeof InventoryService !== 'undefined' ? InventoryService.getItemById(i.itemId) : null;
                return `${item ? item.name : 'Artículo'} ×${i.quantity}`;
            });
            const dates = r.dates?.from ? `${day(r.dates.from)} → ${day(r.dates.to)}` : `Pedida el ${day(r.createdAt)}`;
            return `<li class="sr-request">
                <div class="sr-request-head"><b>${Utils.sanitizeHTML(project?.name || 'Individual')}</b><span class="wh-status-badge ${this.getStatusBadge(r.status)}">${Utils.sanitizeHTML(labels[r.status] || 'Desconocido')}</span></div>
                ${items.length ? `<span class="sr-request-items">${Utils.sanitizeHTML(items.join(' · '))}</span>` : ''}
                <span class="sr-request-dates">${Utils.sanitizeHTML(dates)}</span>
            </li>`;
        }).join('')}</ul>`;
    },
    // ===================================
    // Warehouse View (Management)
    // ===================================
    // Staff board (diseño A, oct. 2026): the four columns of always, each card with its
    // project, material and dates; text buttons for the next step; late returns in red.
    _search: '',
    _showRejected: false,
    _returnedAll: false,

    setSearch(value) {
        this._search = String(value || '').trim().toLowerCase();
        this._refreshBoard();
        const input = document.getElementById('whr-search');
        if (input) { input.focus(); input.setSelectionRange(input.value.length, input.value.length); }
    },
    toggleRejected() { this._showRejected = !this._showRejected; this._refreshBoard(); },
    setReturnedRange(value) { this._returnedAll = value === 'all'; this._refreshBoard(); },
    _refreshBoard() {
        const container = document.getElementById('warehouse-requests-container');
        if (container) SafeHTML.set(container, this.renderWarehouseView());
    },

    _itemsOf(request) {
        return (request.items || []).map(i => {
            const item = typeof InventoryService !== 'undefined' ? InventoryService.getItemById(i.itemId) : null;
            return { name: item ? item.name : 'Artículo desconocido', quantity: i.quantity };
        });
    },
    _matches(request) {
        if (!this._search) return true;
        const project = request.projectId ? ProjectService.getProjectById(request.projectId) : null;
        const text = [request.studentName, project?.name, ...this._itemsOf(request).map(i => i.name)].join(' ').toLowerCase();
        return text.includes(this._search);
    },
    _day(value, withTime) {
        const d = new Date(value);
        if (isNaN(d)) return '';
        const today = new Date(), sameDay = d.toDateString() === today.toDateString();
        const day = sameDay ? 'hoy' : d.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric' });
        const time = withTime && /T\d{2}:\d{2}/.test(String(value)) ? ` ${String(value).slice(11, 16)}` : '';
        return day + time;
    },
    _isLate(request) {
        return request.status === 'picked_up' && request.dates?.to && new Date(request.dates.to) < new Date();
    },

    renderWarehouseView() {
        const requests = WarehouseService.getAllRequests().filter(r => this._matches(r));
        const rejected = requests.filter(r => r.status === 'rejected');
        return `
            <div class="warehouse-requests-manager whr-view">
                <div class="whr-bar">
                    <input type="search" id="whr-search" class="whr-input" placeholder="Buscar alumno, proyecto o material" aria-label="Buscar solicitudes" value="${Utils.sanitizeHTML(this._search)}"
                        ${UIEvents.attr("input", function(event,captured){StudentRequests.setSearch(this.value);}, [])}>
                    <select class="whr-input" aria-label="Devueltas que se muestran" ${UIEvents.attr("change", function(event,captured){StudentRequests.setReturnedRange(this.value);}, [])}>
                        <option value="30" ${this._returnedAll ? '' : 'selected'}>Devueltas: últimos 30 días</option>
                        <option value="all" ${this._returnedAll ? 'selected' : ''}>Devueltas: todas</option>
                    </select>
                </div>
                <div class="kanban-board whr-board">
                    ${this.renderKanbanColumn('Pendientes', 'pending', requests)}
                    ${this.renderKanbanColumn('Aprobadas · por recoger', 'approved', requests)}
                    ${this.renderKanbanColumn('Fuera · prestadas', 'picked_up', requests)}
                    ${this.renderKanbanColumn('Devueltas', 'returned', requests)}
                </div>
                ${rejected.length ? `
                <div class="whr-rejected">
                    <button type="button" class="whr-btn" aria-expanded="${this._showRejected}" ${UIEvents.attr("click", function(event,captured){StudentRequests.toggleRejected();}, [])}>
                        ${this._showRejected ? 'Ocultar rechazadas' : `Ver rechazadas (${rejected.length})`}
                    </button>
                    ${this._showRejected ? `<div class="whr-rejected-list">${rejected.map(r => this.renderRequestCard(r)).join('')}</div>` : ''}
                </div>` : ''}
            </div>
        `;
    },

    renderKanbanColumn(title, status, allRequests) {
        const monthAgo = Date.now() - 30 * 86400000;
        const time = v => new Date(v || 0).getTime() || 0;
        let filtered = allRequests.filter(r => r.status === status);
        if (status === 'returned' && !this._returnedAll) filtered = filtered.filter(r => time(r.updatedAt || r.dates?.to) >= monthAgo);
        const sorters = {
            pending: (a, b) => time(a.createdAt) - time(b.createdAt),
            approved: (a, b) => time(a.dates?.from) - time(b.dates?.from),
            picked_up: (a, b) => time(a.dates?.to) - time(b.dates?.to),
            returned: (a, b) => time(b.updatedAt || b.dates?.to) - time(a.updatedAt || a.dates?.to)
        };
        filtered.sort(sorters[status] || (() => 0));
        const late = filtered.filter(r => this._isLate(r)).length;
        return `
            <section class="kanban-col whr-col is-${status}" aria-label="${Utils.sanitizeHTML(title)}">
                <header class="whr-col-head">
                    <span>${Utils.sanitizeHTML(title)}</span>
                    ${late ? `<small class="whr-late-count">${late} ${late === 1 ? 'atrasada' : 'atrasadas'}</small>` : ''}
                    <b>${filtered.length}</b>
                </header>
                <div class="kanban-items whr-items">
                    ${filtered.length ? filtered.map(r => this.renderRequestCard(r)).join('') : '<p class="whr-empty">Sin solicitudes</p>'}
                </div>
            </section>
        `;
    },

    renderRequestCard(request) {
        const esc = v => Utils.sanitizeHTML(String(v ?? ''));
        const project = request.projectId ? ProjectService.getProjectById(request.projectId) : null;
        const items = this._itemsOf(request);
        const itemsText = items.slice(0, 3).map(i => `${i.name} ×${i.quantity}`).join(' · ') + (items.length > 3 ? ` · +${items.length - 3} más` : '');
        const late = this._isLate(request);
        const from = request.dates?.from, to = request.dates?.to;
        let when = '', dates = '';
        if (request.status === 'pending') {
            const days = Math.floor((Date.now() - new Date(request.createdAt).getTime()) / 86400000);
            when = isNaN(days) ? '' : days <= 0 ? 'Pidió hoy' : days === 1 ? 'Ayer' : `Hace ${days} d`;
            dates = `Recoge ${this._day(from)} · devuelve ${this._day(to)}`;
        } else if (request.status === 'approved') {
            when = this._day(from);
            dates = `Recoge ${this._day(from, true)} · devuelve ${this._day(to)}`;
        } else if (request.status === 'picked_up') {
            when = late ? 'Atrasada' : this._day(to);
            dates = late ? `Devolvía ${this._day(to, true)}` : `Devuelve ${this._day(to, true)}`;
        } else if (request.status === 'returned') {
            when = this._day(request.updatedAt || to);
            dates = `Devuelta el ${this._day(request.updatedAt || to)}`;
        } else if (request.status === 'rejected') {
            when = 'Rechazada';
            dates = `Pedía ${this._day(from)} → ${this._day(to)}`;
        }
        const canDelete = AuthManager.canManageWarehouse() && !(AuthManager.isStudent() && request.studentId === AuthManager.getStudentId());
        return `
            <article class="request-card whr-card ${late ? 'is-late' : ''}">
                <div class="whr-card-head"><b>${esc(request.studentName)}</b><span class="${late ? 'is-late' : ''}">${esc(when)}</span></div>
                ${project ? `<span class="whr-project">${esc(project.name)}</span>` : ''}
                ${items.length ? `<span class="whr-items-text">${esc(itemsText)}</span>` : ''}
                ${dates ? `<span class="whr-dates ${late ? 'is-late' : ''}">${esc(dates)}</span>` : ''}
                <div class="whr-actions">
                    ${this.getActionsForStatus(request)}
                    <span class="whr-spacer"></span>
                    ${project && project.participants && project.participants.length ? `<button type="button" class="whr-btn" title="Ver participantes" aria-label="Participantes de ${esc(project.name)}" ${UIEvents.attr("click", function(event,captured){StudentRequests.viewProjectParticipants(String(captured[0]));}, [project.id])}><i class="fas fa-users"></i></button>` : ''}
                    <button type="button" class="whr-btn" ${UIEvents.attr("click", function(event,captured){StudentRequests.viewDetails(String(captured[0]));}, [request.id])}>Ver</button>
                    ${canDelete ? `<button type="button" class="whr-btn is-danger" title="Eliminar" aria-label="Eliminar la solicitud de ${esc(request.studentName)}" ${UIEvents.attr("click", function(event,captured){StudentRequests.deleteRequest(String(captured[0]));}, [request.id])}><i class="fas fa-trash-alt"></i></button>` : ''}
                </div>
            </article>
        `;
    },

    getActionsForStatus(request) {
        if(AuthManager.isStudent() && request.studentId===AuthManager.getStudentId())return '<span class="whr-note">Debe gestionarlo otro responsable</span>';
        const button = (label, status, kind) => `<button type="button" class="whr-btn ${kind || ''}" ${UIEvents.attr("click", function(event,captured){StudentRequests.updateStatus(String(captured[0]),String(captured[1]));}, [request.id, status])}>${label}</button>`;
        if (request.status === 'pending') return button('Aprobar', 'approved', 'is-go') + button('Rechazar', 'rejected', 'is-no');
        if (request.status === 'approved') return button('Entregar', 'picked_up', 'is-go');
        if (request.status === 'picked_up') return button('Recibir devolución', 'returned', 'is-go');
        return '';
    },

    // ===================================
    // Logic & Events
    // ===================================

    init() {
        const form = document.getElementById('student-request-form');
        if (!form) return;
        // Toggle quantity input when checkbox checked
        form.onchange = (e) => {
            if (e.target.name === 'items') {
                const card = e.target.closest('.checklist-item');
                const qtyControl = card.querySelector('.qty-control');
                const qtyInput = card.querySelector('input[type="number"]');
                if (e.target.checked) {
                    qtyControl.style.display = 'flex';
                    qtyInput.value = 1;
                    qtyInput.required = true;
                } else {
                    qtyControl.style.display = 'none';
                    qtyInput.value = '';
                    qtyInput.required = false;
                }
            }
            StudentRequests.renderOrder();
        };
        this.handleProjectChange(document.getElementById('project-selector').value);
        this.renderOrder();
    },

    // «Tu pedido»: what is chosen, how many, and the dates it will use.
    renderOrder() {
        const form = document.getElementById('student-request-form');
        if (!form) return;
        const checked = [...form.querySelectorAll('input[name="items"]:checked')];
        const summary = document.getElementById('sr-selection-summary');
        if (summary) summary.textContent = checked.length ? `${checked.length} ${checked.length === 1 ? 'artículo seleccionado' : 'artículos seleccionados'}` : 'Ningún artículo seleccionado';
        const list = document.getElementById('sr-order-list');
        if (list) SafeHTML.set(list, checked.map(cb => {
            const item = InventoryService.getItemById(cb.value);
            const qty = document.getElementById(`qty-${cb.value}`)?.value || 1;
            return `<li><b>${Utils.sanitizeHTML(String(qty))}×</b> ${Utils.sanitizeHTML(item ? item.name : cb.value)}</li>`;
        }).join(''));
        const dates = document.getElementById('sr-order-dates');
        if (dates) {
            const project = ProjectService.getProjectById(document.getElementById('project-selector')?.value);
            const from = project?.dates?.from || form.elements.fromDate?.value, to = project?.dates?.to || form.elements.toDate?.value;
            const fmt = v => { const d = new Date(v); return isNaN(d) ? '' : d.toLocaleString('es-ES', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }); };
            dates.textContent = from && to ? `Recoges ${fmt(from)} · devuelves ${fmt(to)}` : 'Elige el proyecto y las fechas.';
        }
    },

    filterMaterial(value) {
        const q = String(value || '').trim().toLowerCase();
        document.querySelectorAll('#student-request-form .sr-item').forEach(card => {
            card.hidden = !!q && !card.textContent.toLowerCase().includes(q);
        });
        document.querySelectorAll('#student-request-form .sr-category').forEach(cat => {
            cat.hidden = ![...cat.querySelectorAll('.sr-item')].some(card => !card.hidden);
        });
    },

    closeTeamPanel() {
        const panel = document.getElementById('sr-team-panel');
        if (!panel) return;
        panel.hidden = true;
        SafeHTML.set(panel, '');
    },

    changeQty(btn, change, max) {
        const input = btn.parentElement.querySelector('input');
        let newVal = parseInt(input.value) + change;
        if (isNaN(newVal)) newVal = 1;
        if (newVal < 1) newVal = 1;
        if (newVal > max) {
            newVal = max;
            Utils.showToast(`Solo hay ${max} unidades disponibles`, 'warning');
        }
        input.value = newVal;
        this.validateQty(input, max);
    },

    validateQty(input, max) {
        if (parseInt(input.value) > max) {
            Utils.showToast(`Solo hay ${max} unidades disponibles`, 'warning');
            input.value = max;
        }
        if (parseInt(input.value) < 1) {
            input.value = 1;
        }
        this.renderOrder();
    },

    handleProjectChange(value) {
        const newProjectContainer = document.getElementById('new-project-container');
        const dateBlock = document.getElementById('date-selection-block');
        const helpText = document.getElementById('project-help-text');
        const dateInputs = dateBlock.querySelectorAll('input[type="datetime-local"]');
        const projectDates = document.getElementById('sr-project-dates');
        if (projectDates) projectDates.hidden = true;
        const team = document.getElementById('sr-project-team');
        if (team) {
            team.hidden = true;
            const project = ProjectService.getProjectById(value);
            if (project) {
                team.hidden = false;
                const canEdit = project.createdBy === AuthManager.getCurrentUser().id && project.status === 'active';
                const me = AuthManager.getStudentId();
                const others = (project.participants || []).filter(id => id !== me).map(id => {
                    const p = WarehouseAPI.participant(id);
                    return p ? `${p.firstName || ''} ${p.lastName || ''}`.trim() : 'Compañero';
                });
                SafeHTML.set(team, `<span class="sr-label sr-team-label">Compañeros de rodaje</span>
                    <span class="sr-chip is-me">Tú</span>
                    ${others.map(n => `<span class="sr-chip">${Utils.sanitizeHTML(n)}</span>`).join('')}
                    ${canEdit ? `<button type="button" class="sr-chip-btn" ${UIEvents.attr('click',function(event,captured){StudentRequests.openParticipantsModal(captured[0]);},[project.id])}>${others.length ? 'Cambiar compañeros' : '+ Añadir compañeros'}</button>` : ''}`);
            }
        }
        this.closeTeamPanel();

        if (value === 'new') {
            // New project: show name input and show dates
            newProjectContainer.style.display = 'block';
            dateBlock.style.display = 'flex';
            if (helpText) helpText.style.display = 'block';
            dateInputs.forEach(i => i.required = true);
            document.getElementById('new-project-name').focus();
        } else if (value !== '') {
            // Existing project selected: hide dates and help text
            newProjectContainer.style.display = 'none';
            dateBlock.style.display = 'none';
            if (helpText) helpText.style.display = 'none';
            dateInputs.forEach(i => i.required = false);

            const project = ProjectService.getProjectById(value);
            if (project && project.dates) {
                if (projectDates) {
                    projectDates.hidden = false;
                    projectDates.textContent = `Fechas del proyecto · Recogida: ${new Date(project.dates.from).toLocaleString('es-ES')} · Devolución: ${new Date(project.dates.to).toLocaleString('es-ES')}`;
                }
            }
        } else {
            // No project selected: show dates and help text
            newProjectContainer.style.display = 'none';
            dateBlock.style.display = 'flex';
            if (helpText) helpText.style.display = 'block';
            dateInputs.forEach(i => i.required = true);
        }
    },

    async openParticipantsModal(projectId = null) {
        const form = document.getElementById('student-request-form');
        if (!form) return;
        const project = projectId ? ProjectService.getProjectById(projectId) : null;
        const user = AuthManager.getCurrentUser();
        if (projectId && (!project || project.createdBy !== user.id || project.status !== 'active')) {
            Utils.showToast('Solo el creador puede cambiar el equipo de un proyecto activo.', 'warning');
            return;
        }
        const name = project ? project.name : document.getElementById('new-project-name').value.trim();
        if (!project && (!name || !form.elements.fromDate.value || !form.elements.toDate.value)) {
            Utils.showToast('Indica el nombre y las fechas del proyecto antes de seleccionar compañeros.', 'warning');
            (!name ? document.getElementById('new-project-name') : !form.elements.fromDate.value ? form.elements.fromDate : form.elements.toDate).focus();
            return;
        }
        const epoch = AuthManager._epoch;
        let open = true;
        const panel = document.getElementById('sr-team-panel');
        if (!panel) return;
        panel.hidden = false;
        SafeHTML.set(panel, '<div id="sr-team-dialog" class="sr-team-dialog" role="status">Cargando compañeros…</div>');
        const container = document.getElementById('sr-team-dialog');
        try {
            const response = await fetch('/api/warehouse/project-peers', {cache:'no-store'});
            const data = await response.json();
            if (!open || !container.isConnected || !form.isConnected || epoch !== AuthManager._epoch) return;
            if (!response.ok || !data.success || !Array.isArray(data.peers)) throw new Error('No se pudieron cargar los compañeros.');
            const peers = data.peers.filter(peer => peer.id !== user.studentId);
            const selected = project?.participants || [];
            for (const id of selected) {
                if (id !== user.studentId && !peers.some(p => p.id === id)) peers.push({id, firstName:'Participante no disponible', lastName:'(desmárcalo para actualizar el equipo)'});
            }
            SafeHTML.set(container, `
                <p>Selecciona a los compañeros de <strong>${Utils.sanitizeHTML(name)}</strong>. Tú ya formas parte del equipo.</p>
                <p class="sr-help">Solo aparecen alumnos activos de tu formación.</p>
                <div class="sr-peer-list">
                    ${peers.length ? peers.map(peer => `<label class="sr-peer">
                        <input type="checkbox" name="project-participant" value="${Utils.sanitizeHTML(peer.id)}" ${selected.includes(peer.id) ? 'checked' : ''}>
                        <span>${Utils.sanitizeHTML(peer.firstName)} ${Utils.sanitizeHTML(peer.lastName || '')}</span>
                    </label>`).join('') : '<p>No hay otros compañeros disponibles en tu formación. Puedes continuar solo.</p>'}
                </div>
                <div class="sr-team-actions">
                    <button type="button" class="btn btn-secondary" ${UIEvents.attr('click',()=>StudentRequests.closeTeamPanel())}>Cancelar</button>
                    <button type="button" class="btn btn-primary" ${UIEvents.attr('click',async function(){
                        if (!open || !form.isConnected || epoch !== AuthManager._epoch || container.dataset.saving) return;
                        container.dataset.saving = 'true';
                        const participants = [...container.querySelectorAll('input[name="project-participant"]:checked')].map(el=>el.value);
                        try {
                            const saved = project ? await ProjectService.updateParticipants(project, participants) : await StudentRequests.saveNewProject(name, participants);
                            if (!saved || epoch !== AuthManager._epoch || !form.isConnected) return;
                            if (open && container.isConnected) StudentRequests.closeTeamPanel();
                            StudentRequests.handleProjectChange(saved.id);
                            Utils.showToast(project ? 'Compañeros actualizados' : 'Proyecto creado con su equipo', 'success');
                        } finally {
                            delete container.dataset.saving;
                        }
                    })}>${project ? 'Guardar compañeros' : 'Crear proyecto'}</button>
                </div>
            `);
        } catch {
            if (!open || !container.isConnected || !form.isConnected || epoch !== AuthManager._epoch) return;
            SafeHTML.set(container, `<p role="alert">No se pudieron cargar los compañeros. No se ha creado ni modificado el proyecto.</p>
                <button type="button" class="btn btn-secondary" ${UIEvents.attr('click',function(event,captured){StudentRequests.openParticipantsModal(captured[0]);},[projectId])}>Reintentar</button>`);
        }
    },

    async saveNewProject(name, participants) {
        const form = document.getElementById('student-request-form');
        if (!form) return null;
        const epoch = AuthManager._epoch;
        const newProj = await ProjectService.createProject({
            name, participants,
            dates: {from:form.elements.fromDate.value, to:form.elements.toDate.value}
        });
        if (!newProj || !form.isConnected || epoch !== AuthManager._epoch) return null;
        // Update only the project selector: preserve chosen material and comments.
        const selector = document.getElementById('project-selector');
        const option = document.createElement('option');
        option.value = newProj.id; option.textContent = newProj.name;
        selector.appendChild(option);
        selector.value = newProj.id;
        return newProj;
    },

    async submitRequest() {
        const form = document.getElementById('student-request-form');
        if (!form || form.dataset.saving === 'true') return;
        if (!form.checkValidity()) {
            form.reportValidity();
            return;
        }

        const formData = new FormData(form);
        const checkedItems = form.querySelectorAll('input[name="items"]:checked');

        if (checkedItems.length === 0) {
            Utils.showToast('Debes seleccionar al menos un artículo', 'warning');
            return;
        }

        // Project check
        const projectId = formData.get('projectId');
        if (!projectId || projectId === 'new') {
            Utils.showToast('Por favor, selecciona o crea un proyecto', 'warning');
            return;
        }

        const items = [];
        checkedItems.forEach(cb => {
            const qtyInput = document.getElementById(`qty-${cb.value}`);
            items.push({
                itemId: cb.value,
                quantity: parseInt(qtyInput.value) || 1
            });
        });

        const user = AuthManager.getCurrentUser();
        const project = projectId ? ProjectService.getProjectById(projectId) : null;

        const requestData = {
            studentId: user.username,
            studentName: user.name,
            projectId: projectId,
            items: items,
            dates: project && project.dates ? project.dates : {
                from: formData.get('fromDate'),
                to: formData.get('toDate')
            },
            comments: formData.get('comments')
        };

        const submit = form.querySelector('[type="submit"]');
        form.dataset.saving = 'true';
        if (submit) { submit.disabled = true; submit.textContent = 'Enviando solicitud…'; }
        let saved;
        try {
            saved = await WarehouseService.createRequest(requestData);
        } finally {
            delete form.dataset.saving;
            if (submit) { submit.disabled = false; submit.textContent = 'Enviar solicitud de material'; }
        }
        if (!saved || !form.isConnected) return;
        Utils.showToast('Solicitud enviada correctamente', 'success');

        // Refresh view
        SafeHTML.set(document.getElementById('content-area'), WarehouseView.render());
        await WarehouseView.init();
        WarehouseView.switchTab(AuthManager.canManageWarehouse()?'personal':'requests');
    },

    updateStatus(requestId, status) {
        const labels = { approved: '¿Aprobar esta solicitud?', rejected: '¿Rechazar esta solicitud?', picked_up: '¿Marcar el material como entregado?', returned: '¿Marcar el material como devuelto?' };
        Utils.showConfirm(labels[status] || '¿Cambiar el estado?', async () => {
            if (!await WarehouseService.updateRequestStatus(requestId, status)) return;
            // Refresh
            WarehouseView.switchTab('requests');
        });
    },

    deleteRequest(requestId) {
        Utils.showConfirm('¿Estás seguro de que deseas eliminar esta solicitud? El proyecto se conservará.', async () => {
            const success = await WarehouseService.deleteRequest(requestId);
            if (success) {
                Utils.showToast('Solicitud eliminada correctamente', 'success');
                // Refresh view
                WarehouseView.switchTab('requests');
            } else {
                Utils.showToast('Error al eliminar la solicitud', 'error');
            }
        });
    },

    viewDetails(requestId) {
        const req = WarehouseService.getAllRequests().find(r => r.id === requestId);
        if (!req) return;

        let itemsHtml = '<div style="display: flex; flex-direction: column; gap: 0.5rem; padding: 0.5rem 0;">';
        req.items.forEach(i => {
            // Check if InventoryService exists, otherwise fallback to WarehouseService per context
            let item = null;
            if (typeof InventoryService !== 'undefined') {
                item = InventoryService.getItemById(i.itemId);
            } else if (typeof WarehouseService !== 'undefined' && WarehouseService.getItem) {
                item = WarehouseService.getItem(i.itemId);
            }

            itemsHtml += `
            <div style="display: flex; justify-content: space-between; align-items: center; padding: 0.6rem 0.8rem; background: #f8f9fa; border-radius: 6px; border: 1px solid #eee;">
                <span style="font-size: 0.9rem; color: #333; font-weight: 500;">${Utils.sanitizeHTML(item ? item.name : 'Artículo desconocido')}</span>
                <span class="wh-status-badge is-primary">x${Utils.sanitizeHTML(i.quantity)}</span>
            </div>`;
        });
        itemsHtml += '</div>';

        const project = req.projectId ? ProjectService.getProjectById(req.projectId) : null;
        let projectName = 'Individual';
        if (project) {
            projectName = project.name;
        } else if (req.projectId) {
            projectName = `Proyecto (ID: ${req.projectId})`;
        }

        let participantsHtml = '';
        if (project && project.participants && project.participants.length > 0) {
            const names = project.participants.map(pId => {
                const s = WarehouseAPI.participant(pId);
                return s ? `${s.firstName} ${s.lastName}` : 'Alumno';
            }).join(', ');

            participantsHtml = `
                <div style="margin-bottom: 1rem;">
                    <div style="font-size: 0.75rem; text-transform: uppercase; color: #888; font-weight: 700; letter-spacing: 0.5px; margin-bottom: 0.2rem;">Participantes</div>
                    <div style="font-size: 0.9rem; color: #555;">${Utils.sanitizeHTML(names)}</div>
                </div>
            `;
        }

        ModalManager.show('Detalles de Solicitud', `
            <div style="font-family: 'Inter', sans-serif;">
                <div style="display: flex; justify-content: flex-end; gap: 0.5rem; margin-bottom: 1rem; align-items: center;">
                    <button class="btn btn-outline-secondary btn-sm" ${UIEvents.attr("click", function(event,captured){StudentRequests.printRequest(String(captured[0]));}, [req.id])} title="Imprimir" style="display: flex; align-items: center; gap: 5px;">
                        <i class="fas fa-print"></i> Imprimir
                    </button>
                    <button class="btn btn-outline-danger btn-sm" ${UIEvents.attr("click", function(event,captured){StudentRequests.downloadPdf(String(captured[0]));}, [req.id])} title="Descargar PDF" style="display: flex; align-items: center; gap: 5px;">
                        <i class="fas fa-file-pdf"></i> Descargar PDF
                    </button>
                </div>
                <div style="background: #fdfdfd; padding: 1rem; border-radius: 8px; border: 1px solid #eee; margin-bottom: 1.5rem;">
                    <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; margin-bottom: 1rem;">
                        <div>
                            <div style="font-size: 0.75rem; text-transform: uppercase; color: #888; font-weight: 700; letter-spacing: 0.5px; margin-bottom: 0.2rem;">Solicitante</div>
                            <div style="font-size: 1rem; color: #333; font-weight: 600;">${Utils.sanitizeHTML(req.studentName)}</div>
                        </div>
                        <div>
                            <div style="font-size: 0.75rem; text-transform: uppercase; color: #888; font-weight: 700; letter-spacing: 0.5px; margin-bottom: 0.2rem;">Proyecto</div>
                            <div style="font-size: 1rem; color: var(--primary-color); font-weight: 600;">${Utils.sanitizeHTML(projectName)}</div>
                        </div>
                    </div>

                    ${participantsHtml}

                    <div>
                        <div style="font-size: 0.75rem; text-transform: uppercase; color: #888; font-weight: 700; letter-spacing: 0.5px; margin-bottom: 0.2rem;">Fechas</div>
                        <div style="font-size: 0.9rem; color: #333;">
                            <i class="far fa-calendar-alt me-1"></i> ${new Date(req.dates.from).toLocaleDateString()} ${new Date(req.dates.from).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            <span class="mx-2 text-muted">&rarr;</span>
                            ${new Date(req.dates.to).toLocaleDateString()} ${new Date(req.dates.to).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </div>
                    </div>
                </div>

                <div style="margin-bottom: 1.5rem;">
                    <div style="font-size: 0.75rem; text-transform: uppercase; color: #888; font-weight: 700; letter-spacing: 0.5px; margin-bottom: 0.5rem;">Comentarios</div>
                    <div style="background: #f8f9fa; padding: 0.8rem; border-radius: 6px; font-style: italic; color: #555; border-left: 3px solid #ddd;">
                        "${Utils.sanitizeHTML(req.comments || 'Sin comentarios adicionales.')}"
                    </div>
                </div>

                <div>
                    <h6 style="font-weight: 700; color: #333; margin-bottom: 0.8rem; border-bottom: 2px solid var(--primary-color); display: inline-block; padding-bottom: 4px;">Material Solicitado</h6>
                    <div style="background: white; border: 1px solid #eee; border-radius: 8px; overflow: hidden;">
                        ${itemsHtml}
                    </div>
                </div>
            </div>
        `);
    },

    printRequest(requestId) {
        const req = WarehouseService.getAllRequests().find(r => r.id === requestId);
        if (!req) return;

        const project = req.projectId ? ProjectService.getProjectById(req.projectId) : null;
        const projectName = project ? project.name : (req.projectId ? `Proyecto ${req.projectId}` : 'Individual');

        let itemsRows = '';
        req.items.forEach(i => {
            let item = null;
            if (typeof InventoryService !== 'undefined') item = InventoryService.getItemById(i.itemId);
            else if (typeof WarehouseService !== 'undefined' && WarehouseService.getItem) item = WarehouseService.getItem(i.itemId);

            itemsRows += `
               <tr style="border-bottom: 1px solid #eee;">
                   <td style="padding: 8px;">${Utils.sanitizeHTML(item ? item.name : 'Unknown')}</td>
                   <td style="padding: 8px; text-align: right;">${Utils.sanitizeHTML(i.quantity)}</td>
               </tr>
           `;
        });

        const content = `
            <div style="font-family: Arial, sans-serif; padding: 20px;">
                <h2 style="color: #333; border-bottom: 2px solid #00A99D; padding-bottom: 10px;">Solicitud de Material - ${Utils.sanitizeHTML(SchoolService.shortName())}</h2>
                <div style="margin-top: 20px; display: grid; grid-template-columns: 1fr 1fr; gap: 20px;">
                    <div>
                        <strong>Solicitante:</strong> ${Utils.sanitizeHTML(req.studentName)}<br>
                        <strong>Proyecto:</strong> ${Utils.sanitizeHTML(projectName)}<br>
                        <strong>Fechas:</strong> ${new Date(req.dates.from).toLocaleDateString()} - ${new Date(req.dates.to).toLocaleDateString()}
                    </div>
                    <div style="text-align: right;">
                        <strong>ID Solicitud:</strong> ${Utils.sanitizeHTML(req.id.substring(0, 8))}<br>
                        <strong>Fecha solicitud:</strong> ${new Date().toLocaleDateString()}
                    </div>
                </div>

                <h3 style="margin-top: 30px; font-size: 1.1rem; background: #f8f9fa; padding: 5px 10px;">Material Solicitado</h3>
                <table style="width: 100%; border-collapse: collapse; margin-top: 10px;">
                    <thead>
                        <tr style="background: #eee;">
                            <th style="text-align: left; padding: 8px;">Artículo</th>
                            <th style="text-align: right; padding: 8px;">Cantidad</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${itemsRows}
                    </tbody>
                </table>

                <div style="margin-top: 30px; border-top: 1px solid #ccc; padding-top: 10px; font-size: 0.9rem; color: #666;">
                    <p>Firma Alumno:</p>
                    <br><br><br>
                    <p style="border-top: 1px dashed #ccc; width: 200px;"></p>
                </div>
            </div>
        `;

        const container = document.getElementById('print-container');
        if (container) {
            SafeHTML.set(container, content);
            document.body.classList.add('printing-modal');

            const cleanUp = () => {
                document.body.classList.remove('printing-modal');
                SafeHTML.set(container, ''); // Clean up content
            };

            window.addEventListener('afterprint', cleanUp, { once: true });

            // Fallback for browsers that don't support afterprint well or if print is cancelled quickly
            // But afterprint is standard now.
            window.print();
        }
    },

    downloadPdf(requestId) {
        if (!Utils.whenPdfReady(() => this.downloadPdf(requestId))) return;

        const req = WarehouseService.getAllRequests().find(r => r.id === requestId);
        if (!req) return;

        const { jsPDF } = window.jspdf;
        const doc = new jsPDF();

        const project = req.projectId ? ProjectService.getProjectById(req.projectId) : null;
        const projectName = project ? project.name : 'Individual';

        // Header
        doc.setFontSize(18);
        doc.setTextColor(0, 169, 157); // Primary color
        doc.text(`${SchoolService.shortName()} - Solicitud de Material`, 105, 20, { align: 'center' });

        doc.setLineWidth(0.5);
        doc.setDrawColor(0, 169, 157);
        doc.line(20, 25, 190, 25);

        // Info
        doc.setFontSize(11);
        doc.setTextColor(0, 0, 0);
        doc.text(`Solicitante: ${req.studentName}`, 20, 40);
        doc.text(`Proyecto: ${projectName}`, 20, 48);
        doc.text(`Fechas: ${new Date(req.dates.from).toLocaleDateString()} - ${new Date(req.dates.to).toLocaleDateString()}`, 20, 56);

        doc.text(`ID: ${req.id.substring(0, 8)}`, 140, 40);
        doc.text(`Generado: ${new Date().toLocaleDateString()}`, 140, 48);

        // Items
        doc.setFontSize(14);
        doc.text('Material Solicitado', 20, 75);

        let y = 85;
        doc.setFontSize(10);
        doc.setFillColor(240, 240, 240);
        doc.rect(20, y - 5, 170, 8, 'F');
        doc.text('Artículo', 25, y);
        doc.text('Cant.', 180, y, { align: 'right' });
        y += 10;

        req.items.forEach(i => {
            let itemName = 'Unknown';
            if (typeof InventoryService !== 'undefined') {
                const item = InventoryService.getItemById(i.itemId);
                if (item) itemName = item.name;
            } else if (typeof WarehouseService !== 'undefined' && WarehouseService.getItem) {
                const item = WarehouseService.getItem(i.itemId);
                if (item) itemName = item.name;
            }

            doc.text(itemName, 25, y);
            doc.text(i.quantity.toString(), 180, y, { align: 'right' });
            y += 8;
        });

        // Footer/Signature
        y += 20;
        doc.text('Firma Alumno:', 20, y);
        doc.setDrawColor(0, 0, 0);
        doc.line(20, y + 25, 80, y + 25); // Signature line

        doc.save(`solicitud_${req.studentName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
    },

    getStatusBadge(status) {
        // Antes devolvía sufijos tipo Bootstrap ("warning"/"info"...) para
        // componer "badge-warning" etc. — esas clases no existen en ningún CSS
        // del proyecto (mismo bug ya visto en Leads/Alumnado/Inventario), así
        // que salían sin color. Ahora devuelve el modificador real de
        // `.wh-status-badge` (ver warehouse.css).
        const map = {
            'pending': 'is-warning',
            'approved': 'is-info',
            'rejected': 'is-danger',
            'picked_up': 'is-primary',
            'returned': 'is-success'
        };
        return map[status] || 'is-secondary';
    },

    viewProjectParticipants(projectId) {
        const project = ProjectService.getProjectById(projectId);
        if (!project) return;

        if (!project.participants || project.participants.length === 0) {
            Utils.showToast('Este proyecto no tiene participantes adicionales', 'info');
            return;
        }

        const participantsList = project.participants.map(pId => {
            const student = WarehouseAPI.participant(pId);
            return student ?
                `<div style="display: flex; align-items: center; padding: 0.8rem 1rem; background: #f8f9fa; border-radius: 6px; border: 1px solid #eee; margin-bottom: 0.5rem;">
                    <div style="width: 32px; height: 32px; background: var(--primary-color); border-radius: 50%; color: white; display: flex; align-items: center; justify-content: center; font-weight: 600; font-size: 0.8rem; margin-right: 1rem;">
                        ${student.firstName.charAt(0)}${student.lastName.charAt(0)}
                    </div>
                    <span style="font-size: 0.95rem; color: #333; font-weight: 500;">${Utils.sanitizeHTML(student.firstName)} ${Utils.sanitizeHTML(student.lastName)}</span>
                </div>`
                : '';
        }).join('');

        ModalManager.show('Participantes del Proyecto', `
            <div style="font-family: 'Inter', sans-serif;">
                <div style="display: flex; justify-content: flex-end; gap: 0.5rem; margin-bottom: 1rem; align-items: center;">
                    <button class="btn btn-outline-secondary btn-sm" ${UIEvents.attr("click", function(event,captured){StudentRequests.printProjectParticipants(String(captured[0]));}, [project.id])} title="Imprimir" style="display: flex; align-items: center; gap: 5px;">
                        <i class="fas fa-print"></i> Imprimir
                    </button>
                    <button class="btn btn-outline-danger btn-sm" ${UIEvents.attr("click", function(event,captured){StudentRequests.downloadProjectParticipantsPdf(String(captured[0]));}, [project.id])} title="Descargar PDF" style="display: flex; align-items: center; gap: 5px;">
                        <i class="fas fa-file-pdf"></i> Descargar PDF
                    </button>
                </div>

                <div style="background: #fdfdfd; padding: 1rem; border-radius: 8px; border: 1px solid #eee; margin-bottom: 1.5rem;">
                    <div style="font-size: 0.75rem; text-transform: uppercase; color: #888; font-weight: 700; letter-spacing: 0.5px; margin-bottom: 0.2rem;">Proyecto</div>
                    <div style="font-size: 1.1rem; color: var(--primary-color); font-weight: 700;">${Utils.sanitizeHTML(project.name)}</div>
                </div>

                <div>
                    <h6 style="font-weight: 700; color: #333; margin-bottom: 0.8rem; border-bottom: 2px solid var(--primary-color); display: inline-block; padding-bottom: 4px;">Listado de Alumnos (${project.participants.length})</h6>
                    <div style="background: white; border: 1px solid #eee; border-radius: 8px; padding: 0.5rem;">
                        ${participantsList}
                    </div>
                </div>
            </div>
        `);
    },

    printProjectParticipants(projectId) {
        const project = ProjectService.getProjectById(projectId);
        if (!project) return;

        const participantsList = project.participants.map(pId => {
            const student = WarehouseAPI.participant(pId);
            return student ? `<li style="padding: 10px 0; border-bottom: 1px solid #eee; font-size: 1rem;">${Utils.sanitizeHTML(student.firstName)} ${Utils.sanitizeHTML(student.lastName)}</li>` : '';
        }).join('');

        const content = `
            <div style="font-family: Arial, sans-serif; padding: 20px;">
                <h2 style="color: #333; border-bottom: 2px solid #00A99D; padding-bottom: 10px;">Participantes del Proyecto</h2>
                <div style="margin-top: 20px;">
                    <h3 style="font-size: 1.4rem; margin-bottom: 5px; color: #00A99D;">${Utils.sanitizeHTML(project.name)}</h3>
                    <p style="color: #666; font-size: 0.9rem;">ID Proyecto: ${Utils.sanitizeHTML(project.id)}</p>
                </div>

                <h3 style="margin-top: 30px; font-size: 1.1rem; background: #f8f9fa; padding: 8px 15px; border-left: 4px solid #00A99D;">Listado de Alumnos (${project.participants.length})</h3>
                <ul style="list-style: none; padding: 0; margin-top: 15px;">
                    ${participantsList}
                </ul>

                <div style="margin-top: 40px; border-top: 1px solid #ccc; padding-top: 10px; font-size: 0.8rem; color: #999; text-align: center;">
                    Documento generado el ${new Date().toLocaleDateString()} a las ${new Date().toLocaleTimeString()}
                </div>
            </div>
        `;

        const container = document.getElementById('print-container');
        if (container) {
            SafeHTML.set(container, content);
            document.body.classList.add('printing-modal');
            const cleanUp = () => {
                document.body.classList.remove('printing-modal');
                SafeHTML.set(container, '');
            };
            window.addEventListener('afterprint', cleanUp, { once: true });
            window.print();
        }
    },

    downloadProjectParticipantsPdf(projectId) {
        if (!Utils.whenPdfReady(() => this.downloadProjectParticipantsPdf(projectId))) return;
        const project = ProjectService.getProjectById(projectId);
        if (!project) return;

        const { jsPDF } = window.jspdf;
        const doc = new jsPDF();

        // Header
        doc.setFontSize(18);
        doc.setTextColor(0, 169, 157);
        doc.text('Participantes del Proyecto', 105, 20, { align: 'center' });

        doc.setLineWidth(0.5);
        doc.setDrawColor(0, 169, 157);
        doc.line(20, 25, 190, 25);

        // Project Info
        doc.setFontSize(14);
        doc.setTextColor(0, 0, 0);
        doc.text(project.name, 20, 40);

        doc.setFontSize(10);
        doc.setTextColor(100, 100, 100);
        doc.text(`ID: ${project.id}`, 20, 46);
        doc.text(`Generado: ${new Date().toLocaleDateString()}`, 140, 46);

        // List
        doc.setFontSize(12);
        doc.setTextColor(0, 0, 0);
        doc.text(`Listado de Alumnos (${project.participants.length})`, 20, 60);

        let y = 70;
        project.participants.forEach(pId => {
            const student = WarehouseAPI.participant(pId);
            if (student) {
                doc.text(`• ${student.firstName} ${student.lastName}`, 25, y);
                y += 8;
                // Simple pagination check
                if (y > 280) {
                    doc.addPage();
                    y = 20;
                }
            }
        });

        doc.save(`participantes_${project.name.replace(/\s+/g, '_')}.pdf`);
    }
};

window.StudentRequests = StudentRequests;
